import { Download, Users } from 'lucide-react';
import { Subscriber } from '../services/projects';
import { trackEvent } from '../firebase';
import { Skeleton } from './ui/Skeleton';
import { ErrorAlert } from './ui/ErrorAlert';
import { EmptyState } from './ui/EmptyState';
import { SectionCard } from './ui/SectionCard';

export type { Subscriber };

interface SubscribersListProps {
  subscribers: Subscriber[];
  loading: boolean;
  error: string | null;
  projectId: string;
  /** When true, shows a new-project empty state encouraging the user to embed the snippet. */
  isNewProject?: boolean;
}

function escapeCsvField(value: string): string {
  if (value.includes('"') || value.includes(',') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/** Returns a human-readable source label from UTM params or referrer. */
function resolveSource(s: Subscriber): string {
  if (s.utm?.utm_source) return s.utm.utm_source;
  if (s.referrer) {
    try {
      return new URL(s.referrer).hostname;
    } catch {
      return s.referrer;
    }
  }
  return '';
}

function exportToCsv(subscribers: Subscriber[], projectId: string): void {
  trackEvent('export_subscribers_csv', { project_id: projectId, count: subscribers.length });
  const header = 'email,subscribedAt,source,utm_medium,utm_campaign,utm_term,utm_content,referrer';
  const rows = subscribers.map((s) => {
    const date = s.subscribedAt ? s.subscribedAt.toISOString() : '';
    return [
      escapeCsvField(s.email),
      date,
      escapeCsvField(s.utm?.utm_source ?? ''),
      escapeCsvField(s.utm?.utm_medium ?? ''),
      escapeCsvField(s.utm?.utm_campaign ?? ''),
      escapeCsvField(s.utm?.utm_term ?? ''),
      escapeCsvField(s.utm?.utm_content ?? ''),
      escapeCsvField(s.referrer ?? ''),
    ].join(',');
  });
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const date = new Date().toISOString().slice(0, 10);
  link.download = `subscribers-${date}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function SubscribersList({
  subscribers,
  loading,
  error,
  projectId,
  isNewProject,
}: SubscribersListProps) {
  const countLabel = !loading && !error ? ` (${subscribers.length})` : '';

  const exportButton =
    !loading && subscribers.length > 0 ? (
      <button
        className="p-1.5 rounded-md text-brand-500 hover:bg-brand-50 transition-colors"
        onClick={() => exportToCsv(subscribers, projectId)}
        type="button"
        title="Export CSV"
        aria-label="Export subscribers as CSV"
      >
        <Download size={16} />
      </button>
    ) : undefined;

  return (
    <SectionCard title={`Subscribers${countLabel}`} actions={exportButton}>
      {loading && (
        <div className="space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      )}

      {error && !loading && <ErrorAlert label="Subscribers unavailable" message={error} />}

      {!loading && !error && subscribers.length === 0 && (
        <EmptyState
          icon={Users}
          heading="No subscribers yet"
          body={
            isNewProject
              ? 'Embed the snippet below to start collecting sign-ups.'
              : 'Subscribers will appear here once people sign up.'
          }
        />
      )}

      {subscribers.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="text-left px-3 py-2 border-b-2 border-gray-200 text-gray-700 font-semibold">
                  Email
                </th>
                <th className="text-left px-3 py-2 border-b-2 border-gray-200 text-gray-700 font-semibold">
                  Signed up
                </th>
                <th className="text-left px-3 py-2 border-b-2 border-gray-200 text-gray-700 font-semibold">
                  Source
                </th>
                <th className="text-left px-3 py-2 border-b-2 border-gray-200 text-gray-700 font-semibold">
                  Campaign
                </th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 border-b border-gray-100 text-gray-600">{s.email}</td>
                  <td className="px-3 py-2 border-b border-gray-100 text-gray-600">
                    {s.subscribedAt ? s.subscribedAt.toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2 border-b border-gray-100 text-gray-500">
                    {resolveSource(s) || '—'}
                  </td>
                  <td className="px-3 py-2 border-b border-gray-100 text-gray-500">
                    {s.utm?.utm_campaign || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}
